import { useEffect, useCallback } from "react";
import { IsLoading, useLoading } from "src/common/components/AppState/IsLoading";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { getSelectedProjectGroup } from "src/features/projectGroups/state/projectGroupsSlice";
import { MediaList } from "src/features/gallery/components/MediaList";
import { MediaDetailContainer } from "src/features/gallery/components/MediaDetail";
import {
  getCurrentModeListGallery,
  getCurrentMediaGalleryId,
  setCurrentMediaSidebar,
  fetchFilteredMedias,
  getMediaSearch,
  getFilteredMedias,
  fetchMediaSearchIndex,
  resetMediaSearch,
} from "src/features/gallery/state/gallerySlice";
import { openSlider } from "src/common/state/slice/modal/modalSlice";
import { PaginationControls } from "src/common/components/PaginationControls/PaginationControls";
import { useGalleryPagination } from "src/features/gallery/useGalleryPagination";
import { MediaSearchPanelContainer } from "src/features/gallery/components/MediaSearchPanel";

export const PROJECT_GROUP_MEDIAS_COMPONENT_ID = "projectGroupMediasComponentId";

const MODAL_ID = "media-detail-slideover";

const ProjectGroupMedias = () => {
  const dispatch = useAppDispatch();

  const { page, limit } = useAppSelector(getMediaSearch);
  const selectedProjectGroup = useAppSelector(getSelectedProjectGroup);
  const projectGroupId = selectedProjectGroup?.id ? Number(selectedProjectGroup.id) : undefined;

  const filteredMedias = useAppSelector(getFilteredMedias);

  const galleryLoadingState = useLoading(PROJECT_GROUP_MEDIAS_COMPONENT_ID);
  const isGalleryLoading = galleryLoadingState < 2;

  const currentModeListGallery = useAppSelector(getCurrentModeListGallery);
  const currentMediaGalleryId = useAppSelector(getCurrentMediaGalleryId);

  const rawHandlePageChange = useGalleryPagination({
    componentId: PROJECT_GROUP_MEDIAS_COMPONENT_ID,
    mode: "projectGroupMedias",
  });

  const handlePageChange = useCallback(
    (arg: number | { newPage?: number }) => {
      if (!projectGroupId) return;
      const base = typeof arg === "number" ? { newPage: arg } : arg ?? {};
      rawHandlePageChange({ ...base, projectGroupId });
    },
    [rawHandlePageChange, projectGroupId],
  );

  useEffect(() => {
    if (!projectGroupId) return;
    const start = (page ?? 0) * (limit ?? 20);
    void dispatch(
      fetchFilteredMedias({
        start,
        limit,
        projectGroupId,
        componentId: PROJECT_GROUP_MEDIAS_COMPONENT_ID,
      }),
    );
  }, [dispatch, projectGroupId, page, limit]);

  useEffect(() => {
    void dispatch(
      fetchMediaSearchIndex({
        projectGroupId,
      }),
    );
  }, []);

  useEffect(() => {
    return () => {
      dispatch(resetMediaSearch({ resetEverything: true }));
    };
  }, [dispatch]);

  const openMediaDetail = useCallback(
    (mediaId: number) => {
      dispatch(openSlider(MODAL_ID));
      dispatch(setCurrentMediaSidebar(mediaId));
    },
    [dispatch],
  );

  return (
    <>
      <div className="mt-[1rem] ml-[1.5rem]">
        <MediaSearchPanelContainer
          allowedMediaTypeOverride=""
          nodeId={"0"}
          projectGroupId={projectGroupId}
          componentId={PROJECT_GROUP_MEDIAS_COMPONENT_ID}
          onSearchChanged={(args) => {
            return handlePageChange({ newPage: 0, ...args });
          }}
        />
      </div>
      <IsLoading componentId={PROJECT_GROUP_MEDIAS_COMPONENT_ID} showSuccess={false}>
        <MediaList
          medias={filteredMedias}
          isModalEdit={false}
          listMode={!!currentModeListGallery}
          openMediaDetail={openMediaDetail}
          currentMedia={currentMediaGalleryId}
          isMediaPickerMode={false}
        />
      </IsLoading>

      <IsLoading componentId={PROJECT_GROUP_MEDIAS_COMPONENT_ID} showSuccess={false}>
        <div className="h-[4rem] w-full border-t">
          <PaginationControls
            currentPage={page}
            totalPages={1}
            onPageChange={handlePageChange}
            isLoading={isGalleryLoading}
            className="w-[97%] self-center"
            galleryMode
          />
        </div>
      </IsLoading>

      <MediaDetailContainer id={MODAL_ID} large={false} />
    </>
  );
};

export default ProjectGroupMedias;
